import React from 'react';
import { PlusIcon, TrashIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

// Types
export type FilterOperator = 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'greater_or_equal' | 'less_or_equal' | 'is_set' | 'is_not_set' | 'in' | 'not_in';

export type FilterCondition = {
  id: string;
  property: string;
  operator: FilterOperator;
  value: any;
};

export type FilterGroup = {
  id: string;
  logicOperator: 'AND' | 'OR';
  conditions: FilterCondition[];
  groups: FilterGroup[];
};

interface AdvancedFilterBuilderProps {
  value: FilterGroup;
  onChange: (value: FilterGroup) => void;
  propertyOptions?: Array<{ value: string; label: string; type?: 'string' | 'number' | 'boolean' | 'date' }>;
  placeholder?: string;
  allowNestedGroups?: boolean;
}

const OPERATORS: Array<{ value: FilterOperator; label: string; valueRequired: boolean }> = [
  { value: 'equals', label: 'Equals', valueRequired: true },
  { value: 'not_equals', label: 'Not Equals', valueRequired: true },
  { value: 'contains', label: 'Contains', valueRequired: true },
  { value: 'not_contains', label: 'Does Not Contain', valueRequired: true },
  { value: 'greater_than', label: 'Greater Than', valueRequired: true },
  { value: 'less_than', label: 'Less Than', valueRequired: true },
  { value: 'greater_or_equal', label: 'Greater or Equal', valueRequired: true },
  { value: 'less_or_equal', label: 'Less or Equal', valueRequired: true },
  { value: 'is_set', label: 'Is Set', valueRequired: false },
  { value: 'is_not_set', label: 'Is Not Set', valueRequired: false },
  { value: 'in', label: 'In List', valueRequired: true },
  { value: 'not_in', label: 'Not In List', valueRequired: true },
];

export default function AdvancedFilterBuilder({
  value,
  onChange,
  propertyOptions = [],
  placeholder = 'Select property',
  allowNestedGroups = true,
}: AdvancedFilterBuilderProps) {
  const { t } = useTranslation('analytics');

  const addCondition = (groupId: string) => {
    const newCondition: FilterCondition = {
      id: `condition_${Date.now()}_${Math.random()}`,
      property: '',
      operator: 'equals',
      value: '',
    };

    const updateGroup = (group: FilterGroup): FilterGroup => {
      if (group.id === groupId) {
        return { ...group, conditions: [...group.conditions, newCondition] };
      }
      return { ...group, groups: group.groups.map(updateGroup) };
    };

    onChange(updateGroup(value));
  };

  const removeCondition = (groupId: string, conditionId: string) => {
    const updateGroup = (group: FilterGroup): FilterGroup => {
      if (group.id === groupId) {
        return { ...group, conditions: group.conditions.filter(c => c.id !== conditionId) };
      }
      return { ...group, groups: group.groups.map(updateGroup) };
    };

    onChange(updateGroup(value));
  };

  const updateCondition = (groupId: string, conditionId: string, updates: Partial<FilterCondition>) => {
    const updateGroup = (group: FilterGroup): FilterGroup => {
      if (group.id === groupId) {
        return {
          ...group,
          conditions: group.conditions.map(c =>
            c.id === conditionId ? { ...c, ...updates } : c
          ),
        };
      }
      return { ...group, groups: group.groups.map(updateGroup) };
    };

    onChange(updateGroup(value));
  };

  const addGroup = (parentGroupId: string) => {
    const newGroup: FilterGroup = {
      id: `group_${Date.now()}_${Math.random()}`,
      logicOperator: 'AND',
      conditions: [],
      groups: [],
    };

    const updateGroup = (group: FilterGroup): FilterGroup => {
      if (group.id === parentGroupId) {
        return { ...group, groups: [...group.groups, newGroup] };
      }
      return { ...group, groups: group.groups.map(updateGroup) };
    };

    onChange(updateGroup(value));
  };

  const removeGroup = (parentGroupId: string, groupIdToRemove: string) => {
    const updateGroup = (group: FilterGroup): FilterGroup => {
      if (group.id === parentGroupId) {
        return { ...group, groups: group.groups.filter(g => g.id !== groupIdToRemove) };
      }
      return { ...group, groups: group.groups.map(updateGroup) };
    };

    onChange(updateGroup(value));
  };

  const toggleLogicOperator = (groupId: string) => {
    const updateGroup = (group: FilterGroup): FilterGroup => {
      if (group.id === groupId) {
        return { ...group, logicOperator: group.logicOperator === 'AND' ? 'OR' : 'AND' };
      }
      return { ...group, groups: group.groups.map(updateGroup) };
    };

    onChange(updateGroup(value));
  };

  const renderGroup = (group: FilterGroup, depth: number = 0, parentId?: string) => {
    // Defensive check to ensure group and its properties exist
    if (!group) return null;

    const conditions = group.conditions || [];
    const groups = group.groups || [];

    const isRootGroup = depth === 0;
    const hasContent = conditions.length > 0 || groups.length > 0;
    const shouldShowOperator = conditions.length + groups.length > 1;

    return (
      <div
        key={group.id}
        className={`
          ${depth > 0 ? 'ml-6 pl-4 border-l-2' : ''}
          ${group.logicOperator === 'AND' ? 'border-blue-300 dark:border-blue-600' : 'border-purple-300 dark:border-purple-600'}
        `}
      >
        <div className="space-y-3">
          {/* Logic Operator Toggle - Always visible */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Match:
              </span>
              <button
                onClick={() => toggleLogicOperator(group.id)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm
                  ${group.logicOperator === 'AND'
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                  }
                `}
              >
                {group.logicOperator}
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {group.logicOperator === 'AND' ? 'ALL conditions' : 'ANY condition'}
              </span>
            </div>
            <span className="text-xs text-gray-400 italic">
              Click to toggle between AND/OR
            </span>
          </div>

          {/* Conditions */}
          {conditions.map((condition, index) => {
            const operator = OPERATORS.find(op => op.value === condition.operator);
            const selectedProperty = propertyOptions.find(p => p.value === condition.property);

            return (
              <React.Fragment key={condition.id}>
                {index > 0 && shouldShowOperator && (
                  <div className="flex items-center">
                    <div className={`
                      px-2 py-0.5 rounded text-xs font-medium
                      ${group.logicOperator === 'AND'
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
                      }
                    `}>
                      {group.logicOperator}
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg group/condition hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  {/* Property Select */}
                  <select
                    value={condition.property}
                    onChange={(e) => updateCondition(group.id, condition.id, { property: e.target.value })}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="">{placeholder}</option>
                    {propertyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  {/* Operator Select */}
                  <select
                    value={condition.operator}
                    onChange={(e) => updateCondition(group.id, condition.id, { operator: e.target.value as FilterOperator })}
                    className="w-40 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500"
                  >
                    {OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>

                  {/* Value Input */}
                  {operator?.valueRequired && (
                    <input
                      type={selectedProperty?.type === 'number' ? 'number' : 'text'}
                      value={condition.value}
                      onChange={(e) => updateCondition(group.id, condition.id, { value: e.target.value })}
                      placeholder={condition.operator === 'in' || condition.operator === 'not_in' ? 'comma,separated,values' : 'Value'}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500"
                    />
                  )}

                  {/* Remove Button */}
                  <button
                    onClick={() => removeCondition(group.id, condition.id)}
                    className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors opacity-0 group-hover/condition:opacity-100"
                    title="Remove condition"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </React.Fragment>
            );
          })}

          {/* Nested Groups */}
          {groups.map((nestedGroup, index) => (
            <React.Fragment key={nestedGroup.id}>
              {(index > 0 || conditions.length > 0) && shouldShowOperator && (
                <div className="flex items-center">
                  <div className={`
                    px-2 py-0.5 rounded text-xs font-medium
                    ${group.logicOperator === 'AND'
                      ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                      : 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
                    }
                  `}>
                    {group.logicOperator}
                  </div>
                </div>
              )}

              <div className="relative group/group">
                <div className="absolute -left-2 top-0 bottom-0 flex items-center opacity-0 group-hover/group:opacity-100 transition-opacity">
                  <button
                    onClick={() => removeGroup(group.id, nestedGroup.id)}
                    className="p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors shadow-lg"
                    title="Remove group"
                  >
                    <TrashIcon className="w-3 h-3" />
                  </button>
                </div>
                {renderGroup(nestedGroup, depth + 1, group.id)}
              </div>
            </React.Fragment>
          ))}

          {/* Empty State */}
          {!hasContent && (
            <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
              {t('cohorts.form.noConditionsFilter')}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center space-x-2 pt-2">
            <button
              onClick={() => addCondition(group.id)}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300 font-medium hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-lg transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Add Condition</span>
            </button>

            {allowNestedGroups && (
              <button
                onClick={() => addGroup(group.id)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
              >
                <AdjustmentsHorizontalIcon className="w-4 h-4" />
                <span>Add Group</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Advanced Filters
          </h3>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Build complex conditions with AND/OR logic
        </div>
      </div>

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
        {renderGroup(value)}
      </div>
    </div>
  );
}
